import {
  ActionIcon,
  Badge,
  Flex,
  Paper,
  Text,
  Group,
  Button,
  LoadingOverlay
} from '@inexture/core';
import {
  useActivateCompanyMutation,
  useGetCompaniesQuery,
  useDeleteCompanyMutation,
  useUpdateCompanyMutation,
  useDownloadCompaniesMutation
} from '@services/company.service.ts';
import { FC, useState, useMemo, useRef, useEffect, useCallback } from 'react';
import AdminHeading from '@components/admin/heading/AdminHeading.tsx';
import { CiEdit } from 'react-icons/ci';
import { MdDeleteOutline, MdDone } from 'react-icons/md';
import CompanyForm from '@/form/company/CompanyForm.tsx';
import {
  CompanyFormT,
  companyFormDefaultValues
} from '@/form/company/company.schema';
import { formatReadableDate } from '@/utils/helper';
import { Link } from 'react-router';
import { popupModal } from '@/components/popup/popupModal';
import { closeAllModals } from '@inexture/modals';
import {
  handleActivateCompany,
  handleUpdateCompany,
  handleDeleteCompany
} from './companies.handlers';
import { CompanyProfile } from '@/types/company.interface';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  provideGlobalGridOptions,
  ColDef
} from 'ag-grid-community';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast } from 'sonner';

ModuleRegistry.registerModules([AllCommunityModule]);

provideGlobalGridOptions({
  theme: 'legacy'
});

const Companies: FC<{ status: 'pending' | 'active' }> = ({ status }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const gridRef = useRef<AgGridReact>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const previousStatusRef = useRef(status);
  const statusChanged = previousStatusRef.current !== status;
  const effectivePage = statusChanged ? 1 : currentPage;

  useEffect(() => {
    if (previousStatusRef.current !== status) {
      setCurrentPage(1);
      setSelectedIds([]);
      previousStatusRef.current = status;
    }
  }, [status]);

  const { data, isLoading } = useGetCompaniesQuery({
    status,
    page: effectivePage,
    page_size: pageSize
  }, {
    refetchOnMountOrArgChange: true
  });

  const [activateCompany, { isLoading: isActivating }] =
    useActivateCompanyMutation();
  const [deleteCompany] = useDeleteCompanyMutation();
  const [updateCompany, { isLoading: isUpdatingCompany }] =
    useUpdateCompanyMutation();
  const [downloadCompanies] = useDownloadCompaniesMutation();

  const [activatingId, setActivatingId] = useState<string | number | null>(
    null
  );
  const [deletingId, setDeletingId] = useState<string | number | null>(null);

  const onActivate = useCallback(
    async (activation_key: string, id: string | number) => {
      await handleActivateCompany({
        activation_key,
        id,
        activateCompanyMutation: activateCompany,
        setActivatingId
      });
    },
    [activateCompany]
  );

  const onSaveCompany = useCallback(
    async (id: string | number, data: CompanyFormT) => {
      await handleUpdateCompany({
        id,
        data,
        updateCompanyMutation: updateCompany,
        onSuccess: () => {
          closeAllModals();
        }
      });
    },
    [updateCompany]
  );

  const onEdit = useCallback(
    (company: CompanyProfile) => {
      const editFormValues: CompanyFormT = {
        ...companyFormDefaultValues,
        first_name: company.first_name || '',
        last_name: company.last_name || '',
        company_name: company.company_name || '',
        company_slug: company.company_slug || '',
        company_registration: company.company_registration || '',
        registration_comments: company.registration_comments || '',
        mobile: company.mobile || '',
        email: company.email || '',
        contact: company.contact || ''
      };

      popupModal({
        title: 'Edit Company',
        onClose: () => {
          closeAllModals();
        },
        children: (
          <CompanyForm
            defaultValues={editFormValues}
            onSubmit={data => onSaveCompany(company.id, data)}
            isLoading={isUpdatingCompany}
            submitButtonText="Save Changes"
            showCancelButton={true}
          />
        )
      });
    },
    [isUpdatingCompany, onSaveCompany]
  );

  const onDelete = useCallback(
    async (id: string) => {
      await handleDeleteCompany({
        id,
        status,
        deleteCompanyMutation: deleteCompany,
        setDeletingId
      });
    },
    [deleteCompany, status]
  );

  const companies = useMemo(() => data?.data?.records || [], [data]);
  const totalPages = data?.data ? Math.ceil(data.data.total / pageSize) : 0;
  const totalRecords = data?.data?.total || 0;

  const defaultColDef: ColDef = {
    flex: 1,
    sortable: true,
    filter: false,
    resizable: true,
    wrapText: true,
    autoHeight: true,
    cellStyle: {
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      lineHeight: '1.5'
    },
    headerClass: 'ag-header-cell-center'
  };

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedIds(companies.map(c => c.id));
      } else {
        setSelectedIds([]);
      }
    },
    [companies]
  );

  const handleSelectRow = useCallback((id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
    }
  }, []);

  const allSelected =
    companies.length > 0 && selectedIds.length === companies.length;
  const someSelected =
    selectedIds.length > 0 && selectedIds.length < companies.length;

  const handleDownloadCompanies = async (format: string) => {
    if (selectedIds.length === 0) {
      toast.error('Selecteer ten minste één bedrijf');
      return;
    }
    try {
      const response = await downloadCompanies({
        ids: selectedIds,
        export_format: format
      }).unwrap();

      const fileUrl = response?.data?.url;
      const filename = response?.data?.filename;
      
      if (!fileUrl) {
        throw new Error('Export file URL not found');
      }

      // Create download link using backend-provided filename
      const link = document.createElement('a');
      link.href = fileUrl;
      link.setAttribute('crossOrigin', 'anonymous');
      link.download = filename
        ? filename
        : `companies-${status}-${new Date()
            .toISOString()
            .split('T')[0]}.${format}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Reset selection after download
      setSelectedIds([]);
      toast.success('Bedrijven succesvol gedownload');
    } catch (err) {
      console.error('Failed to download companies', err);
      toast.error('Het downloaden van de bedrijfsgegevens is mislukt.');
    }
  };

  const columnDefs: ColDef<CompanyProfile>[] = useMemo(
    () => [
      {
        headerName: '',
        field: undefined,
        minWidth: 50,
        maxWidth: 50,
        checkboxSelection: false,
        headerCheckboxSelection: false,
        pinned: 'left',
        lockPosition: true,
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-header-cell-center',
        cellRenderer: (params: any) => {
          const company = params.data;
          const isChecked = selectedIds.includes(company.id);
          return (
            <Flex h="100%" align="center" justify="center">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={e => handleSelectRow(company.id, e.target.checked)}
                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
              />
            </Flex>
          );
        },
        headerComponent: () => (
          <Flex h="100%" align="center" justify="center">
            <input
              type="checkbox"
              checked={allSelected}
              ref={input => {
                if (input) {
                  input.indeterminate = someSelected;
                }
              }}
              onChange={e => handleSelectAll(e.target.checked)}
              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
            />
          </Flex>
        )
      } as ColDef<CompanyProfile>,
      {
        headerName: 'Username',
        field: 'username',
        minWidth: 200,
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-header-cell-center',
        cellRenderer: (params: any) => {
          const company = params.data;
          return (
            <Flex h="100%" align="center" justify="center">
              <Link
                to={
                  status === 'pending'
                    ? `/admin/company/pending/${company.id}`
                    : `/admin/company/active/${company.id}`
                }
                style={{
                  color: '#312e81',
                  textDecoration: 'none'
                }}
              >
                <Text fz="sm" truncate title={company.username}>
                  {company.username}
                </Text>
              </Link>
            </Flex>
          );
        }
      },
      {
        headerName: 'Trading Name',
        field: 'company_name',
        minWidth: 200,
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-header-cell-center'
      },
      {
        headerName: 'Domain Name',
        field: 'company_slug',
        minWidth: 200,
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-header-cell-center'
      },
      {
        headerName: 'Email',
        field: 'email',
        minWidth: 200,
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-header-cell-center'
      },
      {
        headerName: 'Mobile',
        field: 'contact',
        minWidth: 150,
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-header-cell-center'
      },
      {
        headerName: 'Date',
        field: 'created_at',
        minWidth: 200,
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-header-cell-center',
        valueFormatter: (params: any) =>
          params.value ? formatReadableDate(params.value) : '-'
      },
      {
        headerName: 'Status',
        field: 'status',
        minWidth: 100,
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-header-cell-center',
        cellRenderer: () => (
          <Badge
            color={status === 'active' ? 'green' : 'yellow'}
            variant="filled"
            radius="sm"
          >
            {status || '-'}
          </Badge>
        )
      },
      ...(status === 'pending'
        ? [
            {
              headerName: 'Action',
              minWidth: 150,
              cellStyle: { textAlign: 'center' },
              headerClass: 'ag-header-cell-center',
              cellRenderer: (params: any) => {
                const company = params.data;
                return (
                  <Flex gap="sm" justify="center" align="center" h="100%">
                    <ActionIcon
                      onClick={() =>
                        onActivate(company.activation_key, company.id)
                      }
                      disabled={
                        (activatingId !== null &&
                          activatingId !== company.id) ||
                        isActivating ||
                        deletingId !== null
                      }
                      color={activatingId === company.id ? 'green' : undefined}
                    >
                      <MdDone />
                    </ActionIcon>

                    <ActionIcon
                      onClick={() => onEdit(company)}
                      disabled={
                        activatingId !== null ||
                        deletingId !== null ||
                        isActivating
                      }
                    >
                      <CiEdit />
                    </ActionIcon>

                    <ActionIcon
                      onClick={() => onDelete(company.id)}
                      disabled={
                        activatingId !== null ||
                        deletingId !== null ||
                        isActivating
                      }
                      color={deletingId === company.id ? 'red' : undefined}
                    >
                      <MdDeleteOutline />
                    </ActionIcon>
                  </Flex>
                );
              }
            } as ColDef<CompanyProfile>
          ]
        : [])
    ],
    [
      status,
      activatingId,
      deletingId,
      onActivate,
      onEdit,
      onDelete,
      selectedIds,
      isActivating,
      handleSelectAll,
      handleSelectRow,
      allSelected,
      someSelected
    ]
  );

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage => currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage => currentPage + 1);
    }
  };

  const handleFirstPage = () => {
    setCurrentPage(1);
  };

  const handleLastPage = () => {
    setCurrentPage(totalPages);
  };

  return (
    <>
      <div>
        <AdminHeading
          title={`${status} Companies`}
          downloadIcon={true}
          onDownload={handleDownloadCompanies}
        />
        <div className="p-5">
          <Paper withBorder radius="md" className="overflow-hidden">
            <style>
              {`
                .ag-header-cell-center .ag-header-cell-label {
                  justify-content: center;
                }
              `}
            </style>
            <div
              className="ag-theme-alpine"
              style={{ height: 'calc(100vh - 250px)', width: '100%' }}
            >
              {isActivating && <LoadingOverlay visible={true} />}
              <AgGridReact
                ref={gridRef}
                rowData={companies}
                columnDefs={columnDefs}
                domLayout="normal"
                defaultColDef={defaultColDef}
                suppressPaginationPanel={true}
                loading={isLoading}
                overlayNoRowsTemplate={
                  isLoading
                    ? '<span>Loading...</span>'
                    : `<span>No ${status} companies found</span>`
                }
                rowHeight={60}
                headerHeight={55}
              />
            </div>
          </Paper>

          <Paper p="md" mt="md" withBorder radius="md">
            <Group justify="space-between">
              <Text
                size="sm"
                c="dimmed"
                key={`pagination-info-${currentPage}-${totalRecords}`}
              >
                Showing {(currentPage - 1) * pageSize + 1} to{' '}
                {Math.min(currentPage * pageSize, totalRecords)} of{' '}
                {totalRecords} entries
              </Text>
              <Group gap="xs">
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleFirstPage}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Text
                  size="sm"
                  fw={500}
                  px="md"
                  key={`page-info-${currentPage}-${totalPages}`}
                >
                  Page {currentPage} of {totalPages}
                </Text>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleLastPage}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </Group>
            </Group>
          </Paper>
        </div>
      </div>
    </>
  );
};

export default Companies;
